/**
 * LandingPage.tsx — Orchestrator shell
 *
 * Renders LandingHero immediately (above fold, in main bundle).
 * Lazy-loads LandingBelowFold so the heavy below-fold content
 * (FAQ × 3 languages, pricing API call, features data) doesn't
 * block first paint.
 *
 * Bundle impact:
 *   Before: LandingPage = 1,027 lines in one chunk
 *   After:  LandingHero (~326 lines) in main bundle
 *           LandingBelowFold (~630 lines) in separate lazy chunk
 */
import React, { Suspense, lazy, useEffect, useRef } from 'react';

import LandingHero from './landing/LandingHero';
import { PageSEOHead } from '../../components/SEOHead';
// Lazy-loaded: keeps analyticsService out of the main bundle
const trackScrollDepth = (page: string, threshold: number) =>
  import('../../services/analyticsService').then(m => m.trackScrollDepth(page, threshold));

const LandingBelowFold = lazy(() => import('./landing/LandingBelowFold'));

// Minimal height placeholder prevents layout shift while BelowFold loads.
// No spinner shown — hero is already fully interactive.
function BelowFoldPlaceholder() {
  return <div style={{ minHeight: '400px' }} aria-hidden="true" />;
}

const SCROLL_THRESHOLDS = [25, 50, 75, 100] as const;

export default function LandingPage() {
  // Track which thresholds have already fired so each one reports only once.
  const firedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      const pct = (scrolled / total) * 100;

      for (const threshold of SCROLL_THRESHOLDS) {
        if (pct >= threshold && !firedRef.current.has(threshold)) {
          firedRef.current.add(threshold);
          trackScrollDepth('home', threshold);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Check immediately in case the page is short enough that 100% is visible on load.
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      <PageSEOHead page="home" />
      <LandingHero />
      <section>
        <Suspense fallback={<BelowFoldPlaceholder />}>
          <LandingBelowFold />
        </Suspense>
      </section>
    </div>
  );
}